import { useState, useEffect } from "react";
import { AlertTriangle, Loader2, MapPin } from "lucide-react";
import axios from "axios";
import Modal from "../Modal/Modal";
import Button from "../Button/Button";
import PaymentForm from "../PaymentForm/PaymentForm";
import { DELIVER_ORDER_URL, UPDATE_PAYMENT_URL } from "../../features/UrlHelper";
import { formatCurrency, toNumber, parseAmountInput, PAYMENT_TYPE_LABELS } from "../PaymentForm/PaymentForm";
import "./DriverPaymentModal.scss";
import { getOrderMapsLink } from "../../features/orderUtils";

// ─── Hlavný komponent ─────────────────────────────────────────────────────────
function DriverPaymentModal({ isOpen, onClose, order, onSuccess }) {
  const [paymentType,     setPaymentType]     = useState("CASH");
  const [amountPaid,      setAmountPaid]      = useState("");
  const [driverNote,      setDriverNote]      = useState("");
  const [isLoading,       setIsLoading]       = useState(false);
  const [errorMessage,    setErrorMessage]    = useState(null);
  const [isEditable,      setIsEditable]      = useState(false);

  const isEditMode   = order?.status === "DELIVERED";
  const totalPrice   = toNumber(order?.total_price);
  const customerSaldo = toNumber(order?.customer_saldo);
  const fixedPaymentType = order?.default_payment_type || null;
  const isPaymentTypeLocked = Boolean(fixedPaymentType);

  // Dynamická kontrola 5-minútového okna na úpravu
  useEffect(() => {
    if (!order) return;

    if (order.status !== "DELIVERED") {
      setIsEditable(true);
      return;
    }

    if (order.status === "DELIVERED" && order.delivered_at) {
      const checkEditableWindow = () => {
        const deliveredTime = new Date(order.delivered_at).getTime();
        const currentTime = new Date().getTime();
        const diffInMinutes = (currentTime - deliveredTime) / (1000 * 60);
        setIsEditable(diffInMinutes < 5);
      };

      checkEditableWindow();
      const interval = setInterval(checkEditableWindow, 15000);
      return () => clearInterval(interval);
    }
  }, [order?.status, order?.delivered_at]);

  // Pri hotovosti rozhoduje zadana suma -- prazdne alebo nezmyselne pole sa
  // nesmie poslat ako 0 €, inak by cela objednavka spadla zakaznikovi do dlhu.
  // Pri ostatnych typoch sumu urci backend sam, posiela sa len informativne.
  const readAmount = () => {
    if (paymentType !== "CASH") return toNumber(amountPaid);
    const amount = parseAmountInput(amountPaid);
    if (amount === null) {
      setErrorMessage("Zadaj platnú zaplatenú sumu (napr. 6,90).");
    }
    return amount;
  };

  const handleSubmit = async () => {
    if (!order) return;

    if (paymentType === "NOT_DELIVERED" && driverNote.trim() === "") {
      setErrorMessage("Pri type 'Nedoručené' je poznámka vodiča povinná.");
      return;
    }

    setErrorMessage(null);
    const receivedAmount = readAmount();
    if (receivedAmount === null) return;

    setIsLoading(true);
    try {
      await axios.put(
        DELIVER_ORDER_URL(order.id),
        {
          payment_type: paymentType,
          received_amount: receivedAmount,
          driver_note: driverNote.trim() || null,
        }
      );
      onSuccess?.();
      onClose?.();
    } catch (err) {
      setErrorMessage(
        err?.response?.data?.message || "Nastala chyba pri ukladaní. Skús to znova."
      );
    } finally {
      setIsLoading(false);
    }
  };

  const handleUpdatePayment = async () => {
    if (!order) return;
    setErrorMessage(null);
    const receivedAmount = readAmount();
    if (receivedAmount === null) return;

    setIsLoading(true);
    try {
      await axios.patch(
        UPDATE_PAYMENT_URL(order.id),
        {
          payment_type: paymentType,
          received_amount: receivedAmount,
        }
      );
      onSuccess?.();
      onClose?.();
    } catch (err) {
      setErrorMessage(
        err?.response?.data?.message || "Nastala chyba pri ukladaní. Skús to znova."
      );
    } finally {
      setIsLoading(false);
    }
  };

  if (!order) return null;

  const items  = (order.items || []).filter((i) => (i.quantity || 1) > 0);
  const saldo  = customerSaldo;
  const saldoText =
    saldo < 0
      ? `${formatCurrency(Math.abs(saldo))} (Dlh)`
      : saldo > 0
      ? `${formatCurrency(saldo)} (Preplatok)`
      : "—";

  const saldoClass = [
    "dpm__info-saldo-value",
    saldo < 0 ? "dpm__info-saldo-value--debt"   : "",
    saldo > 0 ? "dpm__info-saldo-value--credit" : "",
  ].filter(Boolean).join(" ");

  const { url: mapsUrl, hasPreciseLocation } = getOrderMapsLink(order);

  const modalFooter = isEditable ? (
    <div className="dpm__footer">
      <Button
        variant="primary"
        disabled={isLoading}
        onClick={isEditMode ? handleUpdatePayment : handleSubmit}
      >
        {isLoading
          ? <><Loader2 size={16} className="dpm__spinner" /> Ukladám…</>
          : isEditMode ? "Uložiť opravu platby" : "Potvrdiť doručenie a platbu"
        }
      </Button>
    </div>
  ) : null;

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Platba a doručenie"
      footer={modalFooter}
      className="dpm__modal-overlay"
    >
      <div className="dpm__body">

        {/* ── Súhrn objednávky ─────────────────────────────────────────── */}
        <div className="dpm__order-info">
          <div className="dpm__info-row">
            <span className="dpm__info-label">Adresa</span>
            <a
              className="dpm__info-address"
              href={mapsUrl}
              target="_blank"
              rel="noopener noreferrer"
            >
              {order.address_code && (
                <span className="dpm__info-code">{order.address_code}</span>
              )}
              {order.delivery_address}
              {hasPreciseLocation && (
                <MapPin size={13} className="dpm__info-pin" title="Spresnená poloha" />
              )}
            </a>
          </div>

          {order.customer_phone && (
            <div className="dpm__info-row">
              <span className="dpm__info-label">Telefón</span>
              <a className="dpm__info-phone" href={`tel:${order.customer_phone}`}>
                {order.customer_phone}
              </a>
            </div>
          )}

          {items.length > 0 && (
            <div className="dpm__info-row">
              <span className="dpm__info-label">Menu</span>
              <div className="dpm__info-items">
                {items.map((item, idx) => (
                  <span key={idx} className="dpm__info-item-chip">
                    {item.quantity || 1}x{" "}
                    <strong className="dpm__info-item-code" title={item.name}>
                      {item.menu_code || item.name}
                    </strong>
                  </span>
                ))}
              </div>
            </div>
          )}

          <div className="dpm__info-row">
            <span className="dpm__info-label">Cena objednávky</span>
            <span className="dpm__info-price">{formatCurrency(totalPrice)}</span>
          </div>

          <div className="dpm__info-row">
            <span className="dpm__info-label">Saldo zákazníka</span>
            <span className={saldoClass}>{saldoText}</span>
          </div>
        </div>

        {/* ── Formulár platby ──────────────────────────────────────────── */}
        {isEditable ? (
          <div className="dpm__form-wrapper">
            <PaymentForm
              paymentType={paymentType}
              setPaymentType={setPaymentType}
              amountPaid={amountPaid}
              setAmountPaid={setAmountPaid}
              order={order}
              isSubmitting={isLoading}
              isEditMode={isEditMode}
              lockPaymentType={isPaymentTypeLocked}
              lockedPaymentTypeHint={isPaymentTypeLocked ? "Pevne nastavená platba zákazníka" : ""}
              showDriverNote={true}
              driverNote={driverNote}
              setDriverNote={setDriverNote}
            />

            {errorMessage && (
              <div className="dpm__error">
                <AlertTriangle size={16} />
                <span>{errorMessage}</span>
              </div>
            )}
          </div>
        ) : (
          <div className="dpm__payment-readonly">
            <span className="dpm__payment-readonly-value">
              Zaplatené: <strong>{formatCurrency(order.amount_paid)}</strong>
              {" "}({PAYMENT_TYPE_LABELS[order.payment_type] || order.payment_type})
            </span>
            <span className="dpm__payment-readonly-note">Úprava už nie je možná</span>
          </div>
        )}

      </div>
    </Modal>
  );
}

export default DriverPaymentModal;
